import React, { useState } from 'react'

import { Context } from 'types/GlobalUserEvents'
import { IDashboardExample } from 'types/GlobalDashboardExample'

import { useAccountSettings } from 'redux/hooks/SignedInAccount'
import { useCustomer } from 'redux/hooks/Customer'
import { useDashboardExampleActions } from 'redux/actions/DashboardExamples'
import { useHistory } from 'react-router-dom'
import { useParams } from 'utils/hooks'
import { useTranslation } from 'language/translator'

import Alert from 'components_new/atoms/Alert'
import Box from 'components_new/atoms/Box'
import Button from 'components_new/atoms/Button'
import Link from 'components_new/atoms/Link'

import GenericThumbnail from 'components_new/molecules/GenericThumbnail'
import LoadingSection from 'components_new/molecules/LoadingSection'
import SimpleDialog from 'components_new/molecules/SimpleDialog'

import { addRef } from 'utils/queryHandler'

import {
  getDashboardImageUrl,
  getThumbnail,
  isMissingPeriodSetting
} from 'utils/functions'
interface ActivateDashboardExampleDialogProps {
  dashboard: IDashboardExample
  onClose: () => void
  open: boolean
  sx?: object
}

const ActivateDashboardExampleDialog = (
  props: ActivateDashboardExampleDialogProps
) => {
  const { dashboard, onClose, open } = props

  const { tryCopyDashboardExample } = useDashboardExampleActions()

  const history = useHistory()

  const params = useParams<{ id: string }>()
  const { t, lang } = useTranslation()

  const { isAdmin, isEconomist } = useAccountSettings()
  const customer = useCustomer()

  const [activating, setActivating] = useState<boolean>(false)

  const showRestrictedPeriodInfo =
    dashboard.data.has_widgets_restricted_to_period &&
    isMissingPeriodSetting(customer)

  const handleActivate = () => {
    setActivating(true)

    // timeout for a "setup experience":
    setTimeout(() => {
      tryCopyDashboardExample(
        params.id,
        dashboard.title[lang],
        (id: string | null, err?: boolean) => {
          if (id && !err) {
            handleCloseDialog()
            history.push(
              addRef(
                `/dashboards/${id}?created_from_example=${params.id}`,
                Context.FROM_EXPLORE
              )
            )
          }
        }
      )
    }, 2500)
  }

  const handleCloseDialog = () => {
    onClose()
    setActivating(false)
  }

  return (
    <>
      {/*-- menus --*/}
      <SimpleDialog
        actions={
          activating ? null : (
            <>
              <Button
                disabled={activating}
                onClick={handleCloseDialog}
                variant="text"
              >
                {t('global', 'cancel')}
              </Button>
              <Button disabled={activating} onClick={handleActivate}>
                {t('global', 'activate')}
              </Button>
            </>
          )
        }
        contentText={
          activating ? undefined : t('dashboards', 'activate_dashboard_info')
        }
        maxWidth="xs"
        onClose={handleCloseDialog}
        open={open}
        title={t('dashboards', 'activate_dashboard')}
      >
        {!activating && showRestrictedPeriodInfo ? (
          <Alert severity="info" sx={{ mb: 2 }}>
            {t('dashboards', 'activate_restricted_period_info')}{' '}
            {isAdmin || isEconomist ? (
              <Link to="/settings/economy">
                {t('dashboards', 'activate_restricted_period_link')}
              </Link>
            ) : (
              t('dashboards', 'activate_restricted_period_no_access')
            )}
          </Alert>
        ) : null}

        <Box
          sx={{
            position: 'relative'
          }}
        >
          {activating ? (
            <Box
              sx={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                height: '100%',
                zIndex: 1,
                bgcolor: 'rgba(255, 255, 255, 0.92)'
              }}
            >
              <LoadingSection
                sx={{ py: 2 }}
                description={[
                  t('dashboards', 'activating_dashboard_loading_1'),
                  t('dashboards', 'activating_dashboard_loading_2')
                ]}
              />
            </Box>
          ) : null}

          <GenericThumbnail
            body={
              dashboard.author.name
                ? dashboard.author.company
                  ? `${dashboard.author.name} · ${dashboard.author.company}`
                  : dashboard.author.name
                : 'Homepal'
            }
            disabled={true}
            href={`/templates/${dashboard.id}`}
            key={dashboard.id}
            media={{ src: getThumbnail(getDashboardImageUrl(dashboard.id)) }}
            search={history.location.search}
            title={dashboard.title[lang]}
            variant={dashboard.author.name ? 'user' : 'homepal'}
          />
        </Box>
      </SimpleDialog>
    </>
  )
}

export default ActivateDashboardExampleDialog
